import { useContext, useEffect, useReducer, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Link,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import { CustomDisabledInput } from "./utils/CustomDisabledInput";
import { CustomCheckBox } from "./utils/CustomCheckBox";

import { Auth } from "aws-amplify";
import { UserContext } from "../../context/UserContext";
import { CourseStructureCtx, DbUser, UserCtx } from "../../interfaces";
import { makeStyles } from "@mui/styles";
import { v2theme } from "../../styles/Theme";
import { generateGtmEvent } from "../../Helpers";
import Axios from "../../services/axios";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { MacsAuthenticator } from "../../hooks/useMacsAuthenticator";
import { _AuthType } from "../../enums";

const useStyle = makeStyles((theme) => ({
  editAccountModal: {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalTitle: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  gridItem: {
    padding: "0px !important",
  },
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
}));

interface editUserModalProps {
  open: boolean;
  handleOpen: () => void;
  handleClose: () => void;
}

const formDefaultState = {
  fields: {
    currentPassword: { value: "", error: "", hasError: false },
    newPassword: { value: "", error: "", hasError: false },
    confirmPassword: { value: "", error: "", hasError: false },
  },
  passwordValidations: {
    minLength: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false,
    hasLowerCase: false,
  },
};

const initialFormState = {
  fields: {
    currentPassword: { value: "", error: "", hasError: false },
    newPassword: { value: "", error: "", hasError: false },
    confirmPassword: { value: "", error: "", hasError: false },
  },
  passwordValidations: {
    minLength: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false,
    hasLowerCase: false,
  },
};

const formReducer = (state: any, action: any) => {
  switch (action.type) {
    case "UPDATE_FORM":
      return {
        ...state,
      };
    case "RESET_FORM":
      return {
        ...formDefaultState,
      };
    default:
      return state;
  }
};

const validateInput = (name: any, value: any, dispatch: any, formState: any) => {
  if (name === "newPassword") {
    formState.passwordValidations.minLength = value.length >= 8;
    formState.passwordValidations.hasNumber = value.match(/[0-9]/g);
    formState.passwordValidations.hasSpecialChar = value.match(/[!@#$%^&*(),.?":{}|<>]/g);
    formState.passwordValidations.hasUpperCase = value.match(/[A-Z]/g);
    formState.passwordValidations.hasLowerCase = value.match(/[a-z]/g);
    if (
      formState.passwordValidations.hasLowerCase &&
      formState.passwordValidations.hasUpperCase &&
      formState.passwordValidations.hasNumber &&
      formState.passwordValidations.hasSpecialChar &&
      formState.passwordValidations.minLength
    ) {
      formState.fields.newPassword.error = "";
      formState.fields.newPassword.hasError = false;
      formState.fields.newPassword.value = value;
      dispatch({ type: "UPDATE_FORM", data: formState });
    } else {
      formState.fields.newPassword.error =
        "Password must contain at least 1 uppercase, 1 lowercase, 1 number, 1 special character and minimum length of 8 characters";
      formState.fields.newPassword.hasError = true;
      dispatch({ type: "UPDATE_FORM", data: formState });
    }
  }
  if (name === "confirmPassword") {
    if (value === formState.fields.newPassword.value) {
      formState.fields.confirmPassword.error = "";
      formState.fields.confirmPassword.hasError = false;
      formState.fields.confirmPassword.value = value;
      dispatch({ type: "UPDATE_FORM", data: formState });
    } else {
      formState.fields.confirmPassword.error = "Passwords do not match";
      formState.fields.confirmPassword.hasError = true;
      dispatch({ type: "UPDATE_FORM", data: formState });
    }
  }
  if (name === "currentPassword") {
    if (value.length !== 0) {
      formState.fields.currentPassword.error = "";
      formState.fields.currentPassword.hasError = false;
      formState.fields.currentPassword.value = value;
      dispatch({ type: "UPDATE_FORM", data: formState });
    } else {
      formState.fields.currentPassword.error = "Current Password is required";
      formState.fields.currentPassword.hasError = true;
      dispatch({ type: "UPDATE_FORM", data: formState });
    }
  }
};

export const EditUserAccountModal = (props: editUserModalProps) => {
  const classes = useStyle();
  const [formState, dispatch] = useReducer(formReducer, initialFormState);
  const [a11y, setA11y] = useState(false);
  const [a11yChanged, setA11yChanged] = useState(false);

  const [learningGPPPref, setLearningGPPPref] = useState<boolean>(false);
  const [learningGPPPrefChanged, setLearningGPPPrefChanged] = useState(false);

  const [isFormSaved, setIsFormSaved] = useState(false);
  const [showUpdatePasswordForm, setShowUpdatePasswordForm] = useState(false);
  const [toggleSaveBtnState, setToggleSaveBtnState] = useState(false);
  const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
  const userCtx = useContext(UserContext) as UserCtx;
  const currentUser: DbUser = userCtx.dbUser;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();

  useEffect(() => {
    if (props.open) {
      setA11yChanged(false);
      setIsFormSaved(false);
      setToggleSaveBtnState(false);
      setShowUpdatePasswordForm(false);
      setA11y(currentUser.accessibility);
      if (typeof currentUser.metadata?.le_screen_gpp_prefer_text === "undefined") {
        setLearningGPPPref(false);
        setLearningGPPPrefChanged(true);
      } else {
        setLearningGPPPref(currentUser.metadata?.le_screen_gpp_prefer_text ?? false);
      }
    }

    return () => {
      dispatch({ type: "RESET_FORM" });
      if (!props.open) {
        setIsPasswordUpdated(false);
      }
    };
  }, [props.open, currentUser, userCtx]);

  useEffect(() => {
    if (isFormSaved) {
      setToggleSaveBtnState(true);
      const toggle = setTimeout(() => {
        setToggleSaveBtnState(false);
      }, 2000);
      return () => {
        clearTimeout(toggle);
      };
    }
  }, [isFormSaved]);

  const formSubmitHandler = async (e: any) => {
    e.preventDefault();
    HandleDataLayer("form", "click", "click", "main content", "edit account", "save");
    if (showUpdatePasswordForm) {
      // proceed to change password and input validation only when user has opened the edit password form
      validateInput(
        "currentPassword",
        formState.fields.currentPassword.value,
        dispatch,
        formState
      );
      validateInput(
        "newPassword",
        formState.fields.newPassword.value,
        dispatch,
        formState
      );
      validateInput(
        "confirmPassword",
        formState.fields.confirmPassword.value,
        dispatch,
        formState
      );
      if (
        formState.fields.currentPassword.hasError ||
        formState.fields.newPassword.hasError ||
        formState.fields.confirmPassword.hasError
      ) {
        //form has error cannot edit user
        console.error("Change password Form has error");
        return;
      } else {
        // everything is correct proceed to edit user change password

        try {
          let currentUser = await Auth.currentAuthenticatedUser();
          await Auth.changePassword(
            currentUser,
            formState.fields.currentPassword.value,
            formState.fields.newPassword.value
          );
          setShowUpdatePasswordForm(false);
          setIsPasswordUpdated(true);
          dispatch({ type: "RESET_FORM" });
          await Axios.put("/password-reset-confirmation-email");
        } catch (e: any) {
          formState.fields.currentPassword.error = e.message;
          formState.fields.currentPassword.hasError = true;
          dispatch({ type: "UPDATE_FORM", data: formState });
        }
      }
    }
    if (a11yChanged) {
      // user is trying to change a11y toggle only and in future we can have logic to you can change other user details
      const userId = userCtx.dbUser?.id;
      try {
        await userCtx.updateDbUser(userId, { accessibility: a11y });
      } catch (e) {
        console.error(e);
      }
    }
    if (learningGPPPrefChanged) {
      // user is trying to change learning pref toggle only and in future we can have logic to you can change other user details
      try {
        await userCtx.updateUserMetadata({ le_screen_gpp_prefer_text: learningGPPPref });
      } catch (e) {
        console.error(e);
      }
    }
    setIsFormSaved(true);
    const learning_pref =
      typeof learningGPPPref === "undefined"
        ? "none"
        : learningGPPPref
        ? "text"
        : "video";

    HandleDataLayer(
      "form",
      "account update",
      "submit",
      "main content",
      "edit account",
      "account updated",
      "onsubmit",
      {
        first_name: currentUser?.first_name,
        last_name: currentUser?.last_name,
        email: currentUser?.email,
        accessibility: a11y,
        learning_preference: learning_pref,
      }
    );
  };

  const vertBreakpointSm = useMediaQuery("(max-height:755px)");
  const horiBreakpointSm = useMediaQuery("(max-width:769px)");
  const HandleDataLayer = (
    event: string,
    action: string,
    type: string,
    region: string,
    section: string,
    text: string,
    name?: string,
    metadata?: any
  ) => {
    generateGtmEvent(
      {
        name: name ? name : "onclick",
        event: event,
        action: action,
        type: type,
        region: region,
        section: section,
        text: text,
      },
      metadata
    );
  };

  const showChangePasswordButton = () => {
    if (MacsAuthenticator.authHook.authType === _AuthType.LTI) {
      return !courseMetadata?.disable_change_password;
    }
    return true;
  };

  const editingDisabled =
    courseMetadata?.hide_learning_preferences &&
    courseMetadata?.hide_accessibility_toggle &&
    MacsAuthenticator.authHook.authType === _AuthType.LTI &&
    courseMetadata?.disable_change_password;

  const editText = editingDisabled ? "Account details" : "Edit account";
  const editTextAriaLabel = `${editText.toLowerCase().replace(" ", "-")}-modal-title`;
  const closeAriaLabel = editingDisabled
    ? "close account details"
    : "close edit user account";

  return (
    <>
      <Modal
        open={props.open}
        onClose={props.handleClose}
        aria-labelledby={editTextAriaLabel}
        role="dialog">
        <Card
          sx={{ width: horiBreakpointSm ? "600px" : "900px" }}
          className={classes.editAccountModal}>
          <CardHeader
            sx={{ padding: "16px 24px" }}
            title={
              <Typography
                id={editTextAriaLabel}
                variant="h2"
                className={classes.modalTitle}>
                {editText}
              </Typography>
            }
            action={
              <Tooltip
                placement="bottom-end"
                title="Close"
                arrow
                role="tooltip"
                describeChild>
                <IconButton
                  onClick={() => {
                    HandleDataLayer(
                      "modal",
                      "close",
                      "click",
                      "main content",
                      "edit account",
                      "close cross"
                    );
                    props.handleClose();
                  }}
                  aria-label={closeAriaLabel}
                  className={classes.closeButton}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <CardContent
            sx={{
              padding: "24px 32px",
              maxHeight: vertBreakpointSm ? "300px" : "400px",
              overflow: "auto",
            }}>
            <Grid container spacing={2}>
              <Grid item xs={6} className={classes.gridItem}>
                <div data-sentry-mask>
                  <CustomDisabledInput
                    label={"First name"}
                    value={currentUser?.first_name}
                  />
                </div>
              </Grid>
              <Grid item xs={6} className={classes.gridItem}>
                <div data-sentry-mask>
                  <CustomDisabledInput
                    label={"Last Name"}
                    value={currentUser?.last_name}
                  />
                </div>
              </Grid>
              <Grid item xs={6} className={classes.gridItem}>
                <div data-sentry-mask>
                  <CustomDisabledInput label={"Email"} value={currentUser?.email} />
                </div>
              </Grid>
            </Grid>
            {showChangePasswordButton() && (
              <>
                <Typography
                  component={"p"}
                  variant="h6"
                  sx={{ color: "#000000", fontWeight: "bold", fontSize: "24px" }}>
                  Password
                </Typography>
                {isPasswordUpdated && (
                  <Typography
                    variant={"body2"}
                    sx={{ color: "#000000", fontSize: "16px", marginTop: "16px" }}>
                    Your password has been changed successfully!
                  </Typography>
                )}
                {showUpdatePasswordForm ? (
                  <Grid sx={{ paddingTop: "16px" }} container={true} spacing={4}>
                    <Grid item xs={6}>
                      <Grid style={{ paddingBottom: "1rem" }} item xs={12}>
                        <TextField
                          className={classes.inputText}
                          error={formState.fields.currentPassword.hasError}
                          helperText={formState.fields.currentPassword.error}
                          type="password"
                          autoComplete="current-password"
                          required={true}
                          fullWidth={true}
                          id="current-password"
                          label="Current Password"
                          variant="outlined"
                          onChange={(e) =>
                            validateInput(
                              "currentPassword",
                              e.target.value,
                              dispatch,
                              formState
                            )
                          }
                        />
                      </Grid>
                      <Grid style={{ paddingBottom: "1rem" }} item xs={12}>
                        <TextField
                          className={classes.inputText}
                          error={formState.fields.newPassword.hasError}
                          helperText={formState.fields.newPassword.error}
                          type="password"
                          required={true}
                          fullWidth={true}
                          id="new-password"
                          label="New Password"
                          variant="outlined"
                          onChange={(e) =>
                            validateInput(
                              "newPassword",
                              e.target.value,
                              dispatch,
                              formState
                            )
                          }
                        />
                      </Grid>
                      <Grid style={{ paddingBottom: "1rem" }} item xs={12}>
                        <TextField
                          className={classes.inputText}
                          error={formState.fields.confirmPassword.hasError}
                          helperText={formState.fields.confirmPassword.error}
                          type="password"
                          required={true}
                          fullWidth={true}
                          id="confirm-password"
                          label="Confirm Password"
                          variant="outlined"
                          onChange={(e) =>
                            validateInput(
                              "confirmPassword",
                              e.target.value,
                              dispatch,
                              formState
                            )
                          }
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#000000",
                          fontWeight: "bold",
                          fontSize: "16px",
                        }}>
                        When setting a password, it must be:
                      </Typography>
                      <Box sx={{ paddingTop: "16px" }}>
                        <Typography
                          variant="body1"
                          sx={{ color: "#000000", fontSize: "16px" }}>
                          {formState.passwordValidations.minLength ? (
                            <CheckIcon sx={{ color: "#000000", fontSize: "16px" }} />
                          ) : (
                            <CloseOutlinedIcon
                              sx={{ color: "#000000", fontSize: "16px" }}
                            />
                          )}{" "}
                          A minimum of 8 characters
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "#000000", fontSize: "16px" }}>
                          {formState.passwordValidations.hasLowerCase ? (
                            <CheckIcon sx={{ color: "#000000", fontSize: "16px" }} />
                          ) : (
                            <CloseOutlinedIcon
                              sx={{ color: "#000000", fontSize: "16px" }}
                            />
                          )}{" "}
                          Contain at least one lowercase letter (a-z)
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "#000000", fontSize: "16px" }}>
                          {formState.passwordValidations.hasUpperCase ? (
                            <CheckIcon sx={{ color: "#000000", fontSize: "16px" }} />
                          ) : (
                            <CloseOutlinedIcon
                              sx={{ color: "#000000", fontSize: "16px" }}
                            />
                          )}{" "}
                          Contain at least one uppercase letter (A-Z)
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "#000000", fontSize: "16px" }}>
                          {formState.passwordValidations.hasSpecialChar ? (
                            <CheckIcon sx={{ color: "#000000", fontSize: "16px" }} />
                          ) : (
                            <CloseOutlinedIcon
                              sx={{ color: "#000000", fontSize: "16px" }}
                            />
                          )}{" "}
                          Contain a symbol (!, @, #, $, etc)
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "#000000", fontSize: "16px" }}>
                          {formState.passwordValidations.hasNumber ? (
                            <CheckIcon sx={{ color: "#000000", fontSize: "16px" }} />
                          ) : (
                            <CloseOutlinedIcon
                              sx={{ color: "#000000", fontSize: "16px" }}
                            />
                          )}{" "}
                          Contain a number (0-9)
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Button
                    size={"small"}
                    variant="outlined"
                    onClick={() => {
                      HandleDataLayer(
                        "collapse",
                        "open",
                        "click",
                        "modal",
                        "edit account ^ password",
                        "change password"
                      );
                      setShowUpdatePasswordForm(true);
                      setIsPasswordUpdated(false);
                    }}
                    sx={{
                      borderRadius: "8px",
                      marginTop: "16px",
                      fontSize: "16px",
                      "&:hover": {
                        transform: "none",
                      },
                    }}>
                    Change password
                  </Button>
                )}
              </>
            )}
            {showUpdatePasswordForm && (
              <Button
                size={"small"}
                variant="outlined"
                onClick={() => {
                  HandleDataLayer(
                    "collapse",
                    "close",
                    "click",
                    "modal",
                    "edit account ^ password",
                    "cancel"
                  );
                  setShowUpdatePasswordForm(false);
                  setIsPasswordUpdated(false);
                }}
                sx={{
                  borderRadius: "8px",
                  marginTop: "16px",
                  fontSize: "16px",
                  "&:hover": {
                    transform: "none",
                  },
                }}>
                Cancel
              </Button>
            )}
            {!courseMetadata?.hide_accessibility_toggle && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginTop: "24px",
                }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#000000",
                    fontWeight: "bold",
                    fontSize: "24px",
                  }}>
                  Accessibility
                </Typography>
                <Typography variant="body1">
                  Turning this on enables keyboard functionality and higher contrast in
                  the problem solving platform.
                </Typography>
                <FormGroup>
                  <CustomCheckBox
                    label={"Enhance accessibility"}
                    checked={a11y}
                    emitDataLayer={(checked) => {
                      HandleDataLayer(
                        "select",
                        checked ? "click" : "unclick",
                        "checkbox",
                        "main content",
                        "accessibility",
                        "enhance accessibility"
                      );
                    }}
                    onChange={() => {
                      setA11y(!a11y);
                      setA11yChanged(true);
                    }}
                  />
                </FormGroup>
              </Box>
            )}
            {!courseMetadata?.hide_learning_preferences && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginTop: "24px",
                }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#000000",
                    fontWeight: "bold",
                    fontSize: "24px",
                  }}>
                  Learning Preference
                </Typography>
                <Typography variant="body1">
                  Choose your preferred learning experience for Guided Practice.
                </Typography>
                <FormGroup>
                  <RadioGroup
                    value={learningGPPPref ?? null}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newVal = (event.target as HTMLInputElement).value === "true";
                      HandleDataLayer(
                        "select",
                        "click",
                        "checkbox",
                        "main content",
                        "learning preference",
                        newVal ? "text" : "video"
                      );
                      setLearningGPPPref(newVal);
                      setLearningGPPPrefChanged(true);
                    }}>
                    <FormControlLabel value={"false"} control={<Radio />} label="Video" />
                    <FormControlLabel value={"true"} control={<Radio />} label="Text" />
                  </RadioGroup>
                </FormGroup>
              </Box>
            )}
          </CardContent>
          <Divider />
          <CardActions sx={{ padding: "24px 48px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                width: "100%",
              }}>
              {editingDisabled ? (
                <Button
                  size="medium"
                  variant="contained"
                  sx={{
                    fontSize: "1.25rem",
                    borderRadius: "16px",
                    width: "109px",
                    height: "62px",
                    padding: "0px",
                    "&:hover": {
                      transform: "none",
                    },
                  }}
                  onClick={() => {
                    HandleDataLayer(
                      "modal",
                      "close",
                      "click",
                      "main content",
                      "edit account",
                      "close button"
                    );
                    props.handleClose();
                  }}
                  aria-label={closeAriaLabel}>
                  Close
                </Button>
              ) : (
                <Link onClick={formSubmitHandler} href="#" underline="none">
                  <Button
                    size="large"
                    variant="contained"
                    sx={{
                      borderRadius: "16px",
                      width: "109px",
                      height: "62px",
                      padding: "0px",
                      "&:hover": {
                        transform: "none",
                      },
                    }}
                    endIcon={isFormSaved && toggleSaveBtnState ? <CheckIcon /> : null}>
                    {isFormSaved && toggleSaveBtnState ? "Saved" : "Save"}
                  </Button>
                </Link>
              )}
            </Box>
          </CardActions>
        </Card>
      </Modal>
    </>
  );
};
